import { Formik } from "formik";
import { ShieldCheck } from "lucide-react";
import { useState } from "react";

import { EmailVerificationSettingsCard } from "@src/features/auth/v2";
import { FormikInput, FormikSelect } from "@src/shared/components/forms";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { Spinner } from "@src/shared/components/ui/spinner";

import {
  PhysicianProfileFormValidationSchema,
  type PhysicianProfileFormValues,
  PhysicianSpecialtySelectItems,
} from "../../../../domain";
import { PhysicianNpiSearchDialogV2 } from "../../../components/v2/PhysicianNpiSearchDialog";

export interface PhysicianAccountSettingsPageV2Props {
  email: string;
  isEmailVerified: boolean;
  initialValues: PhysicianProfileFormValues;
  npi?: string | null;
  isSendingVerificationEmail?: boolean;
  isNpiVerified?: boolean;
  isSaving?: boolean;
  verificationEmailError?: string | null;
  verificationEmailSent?: boolean;
  saveError?: string | null;
  onSendVerificationEmail: () => void | Promise<void>;
  onSubmit: (values: PhysicianProfileFormValues) => void | Promise<void>;
  onNpiVerified: (npi: string) => void | Promise<void>;
}

export function PhysicianAccountSettingsPageV2({
  email,
  isEmailVerified,
  initialValues,
  npi = null,
  isSendingVerificationEmail = false,
  isNpiVerified = false,
  isSaving = false,
  verificationEmailError = null,
  verificationEmailSent = false,
  saveError = null,
  onSendVerificationEmail,
  onSubmit,
  onNpiVerified,
}: PhysicianAccountSettingsPageV2Props) {
  const [npiDialogOpen, setNpiDialogOpen] = useState(false);

  return (
    <VerticalFlexbox
      className="mx-auto w-full max-w-[720px] px-4 py-6 md-stitch:px-8 md-stitch:py-8"
      gap="none"
    >
      <HeaderText tag="h2" className="mb-8 text-[22px] tracking-tight">
        Account
      </HeaderText>

      <EmailVerificationSettingsCard
        email={email}
        isSendingVerificationEmail={isSendingVerificationEmail}
        isVerified={isEmailVerified}
        onSendVerificationEmail={onSendVerificationEmail}
        verificationEmailError={verificationEmailError}
        verificationEmailSent={verificationEmailSent}
      />

      {isNpiVerified ? (
        <HorizontalFlexbox
          alignItems="center"
          aria-labelledby="physician-npi-banner-heading"
          as="section"
          className="mb-8 flex-col rounded-[12px] border border-border bg-background px-5 py-4 sm-stitch:flex-row"
          gap="md"
          justifyContent="space-between"
        >
          <HorizontalFlexbox alignItems="center" className="min-w-0" gap="md">
            <ShieldCheck aria-hidden="true" className="size-4 text-primary" />
            <VerticalFlexbox className="min-w-0" gap="none">
              <Text
                as="h3"
                fontWeight={600}
                id="physician-npi-banner-heading"
                sizing="sm"
              >
                NPI Verified
              </Text>
              <Text appearance="subdued" sizing="sm">
                {npi}
              </Text>
            </VerticalFlexbox>
          </HorizontalFlexbox>
          <Button
            className="shrink-0"
            disabled={isSaving}
            onClick={() => setNpiDialogOpen(true)}
            type="button"
            variant="outline"
          >
            Change NPI
          </Button>
        </HorizontalFlexbox>
      ) : (
        <HorizontalFlexbox
          alignItems="center"
          aria-labelledby="physician-npi-banner-heading"
          as="section"
          className="mb-8 flex-col rounded-[12px] border border-border bg-background px-5 py-4 sm-stitch:flex-row"
          gap="md"
          justifyContent="space-between"
        >
          <VerticalFlexbox gap="xs">
            <Text
              as="h3"
              fontWeight={600}
              id="physician-npi-banner-heading"
              sizing="sm"
            >
              Verify your NPI
            </Text>
            <Text appearance="subdued" sizing="sm">
              Verify your NPI to double your usage limits.
            </Text>
          </VerticalFlexbox>
          <Button
            className="shrink-0"
            disabled={isSaving}
            onClick={() => setNpiDialogOpen(true)}
            type="button"
          >
            <ShieldCheck aria-hidden="true" className="size-4" />
            Verify NPI
          </Button>
        </HorizontalFlexbox>
      )}

      <PhysicianNpiSearchDialogV2
        initialFirstName={initialValues.firstName}
        initialLastName={initialValues.lastName}
        onNpiVerified={onNpiVerified}
        onOpenChange={setNpiDialogOpen}
        open={npiDialogOpen}
      />

      <Formik<PhysicianProfileFormValues>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={PhysicianProfileFormValidationSchema}
        onSubmit={async (values) => {
          await onSubmit(values);
        }}
      >
        {({ dirty, handleSubmit, isSubmitting }) => {
          const submitting = isSaving || isSubmitting;

          return (
            <VerticalFlexbox as="form" gap="none" onSubmit={handleSubmit}>
              <VerticalFlexbox
                aria-labelledby="physician-basic-info-heading"
                as="section"
                gap="md"
              >
                <Text
                  as="h3"
                  fontWeight={600}
                  id="physician-basic-info-heading"
                  sizing="sm"
                >
                  Basic information
                </Text>

                <HorizontalFlexbox
                  className="flex-col sm-stitch:flex-row"
                  gap="md"
                >
                  <FormikInput
                    autoComplete="given-name"
                    containerClassName="min-w-0 flex-1"
                    disabled={submitting}
                    label="First name"
                    name="firstName"
                    required
                  />
                  <FormikInput
                    autoComplete="additional-name"
                    containerClassName="min-w-0 flex-1"
                    disabled={submitting}
                    label="Middle name"
                    name="middleName"
                  />
                  <FormikInput
                    autoComplete="family-name"
                    containerClassName="min-w-0 flex-1"
                    disabled={submitting}
                    label="Last name"
                    name="lastName"
                    required
                  />
                </HorizontalFlexbox>

                <FormikSelect
                  disabled={submitting}
                  items={PhysicianSpecialtySelectItems}
                  label="Specialty"
                  name="specialty"
                  placeholder="Specialty"
                />

                {saveError ? (
                  <Text appearance="danger" role="alert" sizing="sm">
                    {saveError}
                  </Text>
                ) : null}

                <Button
                  className="w-full sm-stitch:w-auto"
                  disabled={!dirty || submitting}
                  type="submit"
                >
                  {submitting ? <Spinner className="mr-1" size="sm" /> : null}
                  {submitting ? "Saving..." : "Update information"}
                </Button>
              </VerticalFlexbox>
            </VerticalFlexbox>
          );
        }}
      </Formik>
    </VerticalFlexbox>
  );
}
